import { useMemo } from 'react';
import { Progress } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import { Alert } from '../common/Modal';
import { ApiRoutes } from '../Common';
import { ONBOARDING_STEPS, ONBOARDING_STEP_CATEGORIES } from './onboardingSteps';

const OnboardingChecklistBody = ({ tenantId }) => {
    const { data: status } = useQuery({
        queryKey: ['tenantOnboardingStatus', tenantId],
        queryFn: () => httpClient.js(ApiRoutes.tenant.onboardingStatus(tenantId)),
        enabled: !!tenantId,
        refetchOnWindowFocus: true,
        refetchInterval: 15000,
    });

    const completed = useMemo(
        () => new Set((status ?? []).filter((step) => step.isComplete).map((step) => step.stepKey)),
        [status],
    );

    const isComplete = (key) => completed.has(key);

    const resolvePath = (navPath) => (typeof navPath === 'function' ? navPath(tenantId) : navPath);

    const grouped = useMemo(
        () => ONBOARDING_STEP_CATEGORIES
            .map((category) => ({
                category,
                steps: ONBOARDING_STEPS.filter((step) => step.category === category),
            }))
            .filter((group) => group.steps.length > 0),
        [],
    );

    const total = ONBOARDING_STEPS.length;
    const done = ONBOARDING_STEPS.filter((step) => isComplete(step.key)).length;
    const allComplete = total > 0 && done === total;

    return (
        <>
            <p className="text-muted">
                Complete these steps to get the new tenant ready to create jobs. Use
                &ldquo;Set up&rdquo; to jump to each area — each step checks off
                automatically once its data exists. You can reopen this checklist any
                time from the tenant list.
            </p>

            <div className="mb-4">
                <div className="d-flex justify-content-between">
                    <small className="text-muted">Progress</small>
                    <small className={allComplete ? 'text-success font-weight-bold' : 'text-muted'}>
                        {`${done} of ${total}`}
                        {allComplete && (
                            <FontAwesomeIcon
                                icon={faCircleCheck}
                                className="text-success ml-1"
                            />
                        )}
                    </small>
                </div>
                {!allComplete && (
                    <Progress
                        value={done}
                        max={total}
                        className="progress-project"
                        barClassName="progress-bar-static"
                        animated={false}
                        striped={false}
                    />
                )}
            </div>

            {grouped.map((group) => (
                <div
                    key={group.category}
                    className="mb-4"
                >
                    <h6 className="text-uppercase text-muted mb-2">{group.category}</h6>

                    {group.steps.map((step) => {
                        const isDone = isComplete(step.key);
                        return (
                            <div
                                key={step.key}
                                className="d-flex align-items-start justify-content-between border rounded p-2 mb-2"
                            >
                                <div className="d-flex align-items-start">
                                    <FontAwesomeIcon
                                        icon={isDone ? faCircleCheck : faCircle}
                                        className={`mr-2 mt-1 ${isDone ? 'text-success' : 'text-muted'}`}
                                        size="lg"
                                    />
                                    <div>
                                        <div>
                                            {step.label}
                                        </div>
                                        <small className="text-muted">{step.description}</small>
                                    </div>
                                </div>

                                {!isDone && (
                                    <Link
                                        to={`${resolvePath(step.navPath)}?switchTenantId=${tenantId}`}
                                        className="btn btn-sm btn-outline-primary ml-2 text-nowrap"
                                        target="_blank"
                                    >
                                        Set up
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
        </>
    );
};

export const showOnboardingChecklist = (tenantId, tenantName) => {
    Alert.show({
        title: `Tenant Onboarding Checklist${tenantName ? ` — ${tenantName}` : ''}`,
        size: 'lg',
        body: <OnboardingChecklistBody tenantId={tenantId} />,
    });
};
